/* ============================================================
   ResearchActivityBridge — surfaces an active market-research
   run in the GLOBAL generation plate (same surface as content
   batches), so the user sees "research is running" from any
   screen, not only inside the Marketing pages.
   Renders nothing; flags-gated; idle probe every 60s, 12s while
   a run is active, plus a visibility re-check. Stable operation
   id (research_<reportId>) keeps remounts/hydration idempotent,
   and terminal transitions only touch operations that exist —
   an old 'ready' report never resurrects a success toast.
   ============================================================ */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';

const ACTIVE = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];
const IDLE_MS = 60000;
const ACTIVE_MS = 12000;
const STAGE_KEY = {
  queued: 'stCollect',
  gathering: 'stCollect',
  analyzing: 'stCompetitors',
  synthesizing: 'stSynthesize',
  verifying: 'stVerify',
};

function researchFlagsOn() {
  try {
    return typeof window !== 'undefined' && (
      window.localStorage.getItem('dathent.research.enabled') !== '0'
      || window.localStorage.getItem('dathent.research.onboarding') === '1'
    );
  } catch (_) {
    return false;
  }
}

export default function ResearchActivityBridge() {
  const { t } = useTranslation();
  const { operations, startOperation, updateOperation, completeOperation } = useGenerationStatus();
  const operationsRef = useRef(operations);
  operationsRef.current = operations;

  useEffect(() => {
    if (!researchFlagsOn()) return undefined;
    let cancelled = false;
    let timer = null;

    const probe = async () => {
      let delay = IDLE_MS;
      try {
        if (document.visibilityState === 'visible') {
          const res = await api.research.list();
          const latest = ((res?.data ?? res)?.reports || [])[0] || null;
          if (cancelled) return;
          if (latest) {
            const opId = `research_${latest.id}`;
            const existing = operationsRef.current?.[opId];
            if (ACTIVE.includes(latest.status)) {
              delay = ACTIVE_MS;
              api.invalidateCache?.('/api/research');
              const label = `${t('research.activityLabel')} — ${t(`research.${STAGE_KEY[latest.status] || 'stCollect'}`).toLowerCase()}`;
              if (!existing || ['succeeded', 'failed', 'cancelled'].includes(existing.status)) {
                startOperation({
                  id: opId,
                  kind: 'research',
                  label,
                  status: 'running',
                  total: null,
                  deeplink: '/app/research',
                  sourceRef: { type: 'job', id: latest.id },
                  expectedDurationMs: 8 * 60000,
                });
              } else if (existing.label !== label) {
                updateOperation(opId, { label });
              }
            } else if (existing && existing.status === 'running') {
              completeOperation(opId, {
                status: latest.status === 'ready' ? 'succeeded' : 'failed',
                error: latest.status === 'ready' ? null : t('research.failedTitle'),
              });
            }
          }
        }
      } catch (_) {
        // transient — retry on the idle cadence
      }
      if (!cancelled) timer = setTimeout(probe, delay);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        if (timer) clearTimeout(timer);
        probe();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    probe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [t, startOperation, updateOperation, completeOperation]);

  return null;
}
